import { ClinicalAssessmentChip, type ClinicalAssessmentChipColor } from './ClinicalAssessmentChip';
import type { ClinicalAssessmentEntry, ClinicalAssessmentParam } from '@/ayurveda/types/ayurveda';

export interface ClinicalAssessmentChipGroupProps {
  label: string;
  parameter: ClinicalAssessmentParam;
  options: readonly string[];
  entries: ClinicalAssessmentEntry[];
  color?: ClinicalAssessmentChipColor;
  onOverride?: (parameter: ClinicalAssessmentParam, value: string) => void;
  /** Set while an override request for this exact parameter is in flight. */
  pending?: boolean;
}

/**
 * The agent can report more than one value for a single field at once (e.g.
 * a mixed constitution like "Vatha, Pitha, Kapha" for Prakruthi) — split on
 * comma/semicolon/slash so every matching chip highlights, not just one.
 * Plain whitespace is deliberately NOT a delimiter since several option
 * labels are themselves multi-word (e.g. "Kapha Pitha").
 */
function parseMultiValue(value: unknown): string[] {
  if (typeof value !== 'string') return [];
  return value
    .split(/[,;/]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function ClinicalAssessmentChipGroup({
  label,
  parameter,
  options,
  entries,
  color = 'sage',
  onOverride,
  pending,
}: ClinicalAssessmentChipGroupProps) {
  const current = entries.find((e) => e.parameter === parameter);
  const selectedValues = current ? parseMultiValue(current.value) : [];

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {label && <span className="font-hanken text-lg font-semibold text-[#585859]">{label}:</span>}
      {options.map((option) => {
        const isSelected = selectedValues.some(
          (value) => value.toLowerCase() === option.toLowerCase()
        );
        return (
          <ClinicalAssessmentChip
            key={option}
            label={option}
            color={color}
            selected={isSelected}
            source={isSelected ? current?.source : undefined}
            disabled={pending}
            onClick={onOverride ? () => onOverride(parameter, option) : undefined}
          />
        );
      })}
    </div>
  );
}
